<template>
  <div class="hazardousCommodities">
    <v-layout class="title" align-baseline justify-space-between>
      <template v-if="!readonly">
        <v-flex xs2>{{ $t('hazardousMaterials') }}:</v-flex>
        <v-flex md2>
          <v-select
            class="table-item"
            hide-details
            :placeholder="$t('dangerousGoods')"
            :items="dangerousGoodsOptions"
            :rules="[showNone || fieldIsRequired('dangerousGoods')]"
            item-text="text"
            :menu-props="{returnValue:'value'}"
            v-model="modelDangerousGoods"
          />
        </v-flex>
      </template>
      <v-flex xs2>
        <v-text-field
          v-if="isBattery"
          hide-details
          :readonly="readonly"
          :label="$t('offerorName')"
          :rules="[fieldIsRequired('offerorName')]"
          class="required_field"
          v-model="modelOfferorName">
        </v-text-field>
        <!-- Dry ice section commented out -->
        <!-- <v-text-field
          v-else-if="isDryIce"
          hide-details
          :readonly="readonly"
          :label="$t('weight')"
          :rules="[fieldIsNumber('weight'), fieldIsRequired('weight')]"
          class="required_field"
          v-model="modelDryIceWeight">
        </v-text-field> -->
      </v-flex>
      <v-flex xs3>
        <v-text-field
          v-if="isBattery"
          hide-details
          :readonly="readonly"
          :label="$t('emergencyContactNumber')"
          :rules="[
            fieldIsRequired('emergencyContactNumber'),
            fieldIsPhone('emergencyContactNumber')
          ]"
          class="required_field"
          v-model="modelEmergencyContactNumber">
        </v-text-field>
        <!-- Dry ice unit selection commented out -->
        <!-- <v-select
          hide-details
          v-else-if="isDryIce"
          :items="units"
          item-text="text"
          item-value="value"
          v-model="modelDryIceUnit"
          :rules="[fieldIsRequired('unit')]"
          :label="$t('unit')"
        >
        </v-select> -->
      </v-flex>
      <!-- Packaging selector for hazardous materials -->
      <v-flex xs2 v-if="isBattery">
        <PackagingSelector
          :packagings="packagings"
          :show-details="false"
          @select="onSelectPackaging"
        />
      </v-flex>
      <v-flex v-bind="{ [`xs${readonly ? 5 : 2}`]: true }"></v-flex>
    </v-layout>

    <!-- Lithium batteries section -->
    <v-card v-if="isLithiumEnabled" class="lithium-batteries-card mt-3">
      <v-card-text>
        <v-layout align-center class="mb-3">
          <v-flex class="title" style="color: #1976D2; font-weight: bold; font-size: 16px;">
            {{ $t('lithiumBatteries') }}
          </v-flex>
          <v-spacer></v-spacer>
          <v-tooltip bottom max-width="400">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" color="primary" class="mr-2">help</v-icon>
            </template>
            <div>{{ $t('lithiumBatteriesHelp') }}</div>
          </v-tooltip>
          <v-switch
            v-model="lithiumEnabled"
            hide-details
            class="mt-0"
            @change="onLithiumToggle"
          ></v-switch>
          <v-icon class="ml-2">battery_charging_full</v-icon>
        </v-layout>

        <p class="grey--text text--darken-1 mb-4" style="font-size: 13px;">
          {{ $t('lithiumBatteriesDescription') }}
        </p>

        <v-layout column>
          <v-checkbox
            v-for="option in lithiumBatteryOptions"
            :key="option.value"
            v-model="selectedLithiumTypes"
            :value="option.value"
            :label="option.label"
            hide-details
            class="mb-2"
            @change="onLithiumTypeChange"
          >
            <template v-slot:label>
              <v-layout align-center>
                <span>{{ option.label }}</span>
                <v-tooltip bottom max-width="300">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="primary" small class="ml-2">help</v-icon>
                  </template>
                  <div>{{ option.tooltip }}</div>
                </v-tooltip>
              </v-layout>
            </template>
          </v-checkbox>
        </v-layout>

        <!-- Error message -->
        <v-alert
          v-if="lithiumError"
          :value="true"
          type="error"
          class="mt-3"
          icon="error"
        >
          {{ $t('lithiumBatteryRequired') }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card v-if="dangerousGoods === 'LITHIUM_BATTERY'" class="lithium-settings-card">
      <v-card-text>
        <v-layout align-center class="mb-3">
          <v-flex>
            <v-checkbox
              v-model="localIsPrintSafetyDocument"
              :label="$t('printSafetyDocument')"
              hide-details
              class="mt-0"
            >
              <template v-slot:append>
                <v-tooltip bottom max-width="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="primary" class="ml-2">help</v-icon>
                  </template>
                  <div>{{ $t('printSafetyDocumentTooltip') }}</div>
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-flex>
        </v-layout>

        <v-layout v-if="localIsPrintSafetyDocument" class="ml-4">
          <v-flex xs6>
            <ETDSelector
              v-model="localSignatureCode"
              :label="$t('signatureImage')"
              :placeholder="$t('selectOrUploadLetterhead')"
              containerWidth="100%"
              @imageUploaded="handleImageUploaded"
              @imageDeleted="handleImageDeleted"
              @change="handleImageChange"
              :rules="[fieldIsRequired('signatureImage')]"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <template v-if="isBattery">
      <v-data-table
        :headers="headers"
        :items="value"
        hide-actions
        class="elevation-2 mt-2"
      >
        <template v-slot:items="props">
          <td class="subheading">{{ props.item.idNumber }}</td>
          <td class="text-xs-left">{{ props.item.dotShippingName }}</td>
          <td class="text-xs-left">{{ props.item.hazardClass }}</td>
          <td class="text-xs-left">{{ convertWeight(props.item.commodityWeight, props.item.isMeasurementMetric) }}{{ unit }}</td>
          <td class="text-xs-left">
            <template v-if="props.item.packaging">
              {{ props.item.packaging.name || '-' }}
              <span class="grey--text text--lighten-1">
                ({{ props.item.packaging.length }}x{{ props.item.packaging.width }}x{{ props.item.packaging.height }}{{ props.item.packaging.isMeasurementMetric ? 'cm' : '"' }})
              </span>
            </template>
            <template v-else>-</template>
          </td>
          <td class="text-xs-center" v-if="!readonly">
            <v-menu offset-y :close-on-content-click="false" :ref="`menu${props.index}`">
              <template v-slot:activator="{ on }">
                <v-btn small icon v-on="on" dark flat color="primary">
                  <v-icon>expand_more</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-tile
                  v-for="(item, index) in packagings"
                  :key="index"
                  @click="selectPackagingForCommodity(item, props.index)"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ item.length }}x{{ item.width }}x{{ item.height }}{{ item.isMeasurementMetric ? 'cm' : '"' }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-menu>
            <v-btn icon @click="deleteCommodity(props.index)"><v-icon>delete</v-icon></v-btn>
          </td>
        </template>
      </v-data-table>
      <v-form v-if="!readonly" ref="form" lazy-validation>
        <v-layout style="margin: 10px 0" align-baseline justify-space-between>
          <v-flex xs6>
            <v-select
              hide-details
              :items="batteries"
              item-text="text"
              item-value="value"
              v-model="form.idNumber"
              :rules="[fieldIsRequired('battery')]"
              :label="$t('dotShippingName')"
            >
              <template #item="{ item }">
                {{ `${item.value} ${item.text}` }}
              </template>
            </v-select>
          </v-flex>
          <v-flex xs2>
            <v-text-field
              hide-details
              :label="$t('commodityWeight')"
              :rules="[
                fieldIsRequired('commodityWeight'),
                fieldIsNumber('commodityWeight'),
                v => v > 0 || this.$t('mustBeGreaterThanZero')
              ]"
              class="required_field"
              v-model="form.commodityWeight"
              :suffix="unit">
            </v-text-field>
          </v-flex>
          <v-flex xs3></v-flex>
        </v-layout>
        <v-layout align-baseline justify-end>
          <v-btn dark color="primary" @click="addCommodity">{{ $t('addCommodity') }}</v-btn>
        </v-layout>
      </v-form>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { checkRules, measurementTools } from '@/utils/tools'
import PackagingSelector from './PackagingSelector'
import ETDSelector from './ETDSelector'

export default {
  name: 'HazardousCommodities',
  components: {
    PackagingSelector,
    ETDSelector
  },
  mixins: [checkRules, measurementTools],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    dangerousGoods: {
      type: String,
      default: ''
    },
    offerorName: {
      type: String,
      default: ''
    },
    emergencyContactNumber: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    dryIceWeight: {
      type: String,
      default: ''
    },
    dryIceUnit: {
      type: String,
      default: ''
    },
    filterItems: {
      type: Array,
      default: () => []
    },
    lithiumBatteryTypes: {
      type: Array,
      default: () => []
    },
    isPrintSafetyDocument: {
      type: Boolean,
      default: true
    },
    signatureCode: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      lithiumEnabled: false,
      selectedLithiumTypes: [],
      lithiumError: false,
      localIsPrintSafetyDocument: true,
      // localSignatureCode: null,
      form: {
        idNumber: '',
        commodityWeight: ''
      }
    }
  },
  computed: {
    localSignatureCode: {
      get () {
        return this.signatureCode
      },
      set (value) {
        this.$emit('update:signatureCode', value)
      }
    },
    ...mapGetters([
      'labelServices',
      'isMeasurementMetric'
    ]),
    isLithiumEnabled () {
      return this.dangerousGoods === 'BATTERY' && this.lithiumEnabled
    },
    lithiumBatteryOptions () {
      return [
        {
          value: 'metal_contained',
          label: this.$t('metalContainedInEquipment'),
          tooltip: this.$t('metalContainedTooltip')
        },
        {
          value: 'metal_packed',
          label: this.$t('metalPackedWithEquipment'),
          tooltip: this.$t('metalPackedTooltip')
        },
        {
          value: 'ion_contained',
          label: this.$t('ionContainedInEquipment'),
          tooltip: this.$t('ionContainedTooltip')
        },
        {
          value: 'ion_packed',
          label: this.$t('ionPackedWithEquipment'),
          tooltip: this.$t('ionPackedTooltip')
        }
      ]
    },
    modelDangerousGoods: {
      get () {
        return this.dangerousGoods
      },
      set (value) {
        if (this.readonly) return
        this.$emit('update:dangerousGoods', value)
        // Reset lithium when dangerous goods changes
        if (value !== 'BATTERY') {
          this.lithiumEnabled = false
          this.selectedLithiumTypes = []
          this.lithiumError = false
        }
      }
    },
    modelOfferorName: {
      get () {
        return this.offerorName
      },
      set (value) {
        if (this.readonly) return
        this.$emit('update:offerorName', value)
      }
    },
    modelEmergencyContactNumber: {
      get () {
        return this.emergencyContactNumber
      },
      set (value) {
        if (this.readonly) return
        this.$emit('update:emergencyContactNumber', value)
      }
    },
    modelDryIceWeight: {
      get () {
        return this.dryIceWeight
      },
      set (value) {
        if (this.readonly) return
        this.$emit('update:dryIceWeight', value)
      }
    },
    modelDryIceUnit: {
      get () {
        return this.dryIceUnit
      },
      set (value) {
        if (this.readonly) return
        this.$emit('update:dryIceUnit', value)
      }
    },
    headers () {
      const headers = [
        { text: this.$t('idNumber'), value: 'idNumber', sortable: false },
        { text: this.$t('dotShippingName'), value: 'dotShippingName', sortable: false },
        { text: this.$t('hazardClass'), value: 'hazardClass', sortable: false },
        { text: `${this.$t('commodityWeight')}(${this.totalBatteryWeight}${this.unit})`, value: 'commodityWeight', sortable: false },
        { text: this.$t('packaging'), value: 'packaging', sortable: false }
      ]
      if (!this.readonly) headers.push({ text: this.$t('actions'), value: '', sortable: false, align: 'center' })
      return headers
    },
    dangerousGoodsOptions () {
      const options = [
        // { text: this.$t('none'), value: '' },
        { text: this.$t('battery'), value: 'BATTERY' },
        { text: this.$t('dryIce'), value: 'DRY_ICE' },
        { text: this.$t('lithiumBatteries'), value: 'LITHIUM_BATTERY' }
      ]
      this.showNone && options.unshift({ text: this.$t('none'), value: '' })
      return this.filterItems.length
        ? options.filter(option => this.filterItems.includes(option.value))
        : options
    },
    showNone () {
      return this.filterItems.includes('')
    },
    batteries () {
      return [
        { text: this.$t('lithiumMetalBatteries'), value: 'UN3090', hazardClass: 9, dotShippingName: 'Lithium metal batteries' },
        { text: this.$t('lithiumIonBatteries'), value: 'UN3480', hazardClass: 9, dotShippingName: 'Lithium ion batteries' },
        { text: this.$t('lithiumMetalBatteriesPackedWithEquipment'), value: 'UN3091', hazardClass: 9, dotShippingName: 'Lithium metal batteries packed with equipment' },
        { text: this.$t('lithiumIonBatteriesPackedWithEquipment'), value: 'UN3481', hazardClass: 9, dotShippingName: 'Lithium ion batteries packed with equipment' }
      ]
    },
    unit () {
      return this.isMeasurementMetric ? 'kg' : 'Lbs'
    },
    isBattery () {
      return this.dangerousGoods === 'BATTERY'
    },
    isDryIce () {
      return this.dangerousGoods === 'DRY_ICE'
    },
    totalBatteryWeight () {
      return this.value.reduce((total, battery) => {
        total += +this.convertWeight(battery.commodityWeight, battery.isMeasurementMetric)
        return total
      }, 0)
    },
    units () {
      return [
        { text: 'LB', value: 'LB' },
        { text: 'KG', value: 'KG' }
      ]
    },
    packagings () {
      return this.$store.getters.tenantPackagingsWithWarehousePackagings || []
    }
  },
  watch: {
    isPrintSafetyDocument: {
      handler (newVal) {
        this.localIsPrintSafetyDocument = newVal
      },
      immediate: true
    },
    localIsPrintSafetyDocument (newVal) {
      this.$emit('update:isPrintSafetyDocument', newVal)
    },
    lithiumEnabled (val) {
      this.$emit('update:lithiumEnabled', val)
    },
    selectedLithiumTypes (val) {
      this.$emit('update:lithiumBatteryTypes', val)
      this.lithiumError = val.length === 0
    }
  },
  methods: {
    onLithiumToggle (val) {
      if (val && this.selectedLithiumTypes.length === 0) {
        this.lithiumError = true
      } else {
        this.lithiumError = false
      }
    },
    onLithiumTypeChange () {
      this.lithiumError = this.selectedLithiumTypes.length === 0
    },
    async addCommodity () {
      const $form = this.$refs.form
      const valid = $form.validate()
      if (!valid) return
      this.$emit('input', [...this.value, this.formatCommodityInfo(this.form)])
      $form.reset()
    },
    formatCommodityInfo ({ idNumber, commodityWeight }) {
      const { dotShippingName, hazardClass } = this.batteries.find(battery => battery.value === idNumber) || {}
      return {
        idNumber,
        commodityWeight: String(+commodityWeight),
        dotShippingName: dotShippingName,
        hazardClass: hazardClass,
        isMeasurementMetric: this.isMeasurementMetric
      }
    },
    deleteCommodity (index) {
      this.$emit('input', this.value.filter((item, i) => i !== index))
    },
    onSelectPackaging (packaging) {
      this.$emit('selectPackaging', packaging)
    },
    selectPackagingForCommodity (packaging, index) {
      const commodities = [...this.value]
      commodities[index] = {
        ...commodities[index],
        packaging: packaging
      }
      this.$emit('input', commodities)
      // Close the menu
      const menuRef = this.$refs[`menu${index}`]
      if (menuRef && menuRef.length) {
        menuRef[0].isActive = false
      }
    },
    // ETDSelector event handlers
    handleImageUploaded (imageData) {
      console.log('Image uploaded:', imageData)
      this.$emit('image-uploaded', imageData)
    },
    handleImageDeleted (imageId) {
      console.log('Image deleted:', imageId)
      this.$emit('image-deleted', imageId)
    },
    handleImageChange (image) {
      console.log('Image changed:', image)
      this.$emit('image-changed', image)
    }
  }
}
</script>

<style scoped>
.lithium-batteries-card {
  border: 1px solid #e0e0e0;
}
</style>



// WEBPACK FOOTER //
// src/components/HazardousCommodities.vue